import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const LeafletMap = ({ location = "both", height = "400px" }) => {
  const locations = {
    herzliya: {
      position: [32.16721951284615, 34.84444527431377],
      title: "מרפאת הרצליה",
      address: "רחוב סוקולוב 27, הרצליה",
      zoom: 16,
    },
    beersheba: {
      position: [31.24529224862607, 34.81328667346476],
      title: "מרפאת באר שבע",
      address: "אליהו נאוי 24 (מתחם וורקיז קומת כניסה), באר שבע",
      zoom: 16,
    },
    both: {
      center: [32.168041356830706, 34.84254438780725],
      zoom: 8,
    },
  };

  // Custom medical-themed marker
  const createCustomIcon = () => {
    return L.divIcon({
      html: `
        <div style="
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #01727a 0%, #0288a3 100%);
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 4px 12px rgba(1, 114, 122, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          transform: translate(-50%, -50%);
        ">
          <div style="
            width: 16px;
            height: 16px;
            background: white;
            border-radius: 50%;
          "></div>
        </div>
      `,
      className: "custom-medical-marker",
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20],
    });
  };

  const createPopupContent = (locationData) => `
    <div style="
      direction: rtl;
      text-align: center;
      padding: 15px;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      min-width: 200px;
    ">
      <h3 style="
        margin: 0 0 8px 0;
        color: #01727a;
        font-size: 16px;
        font-weight: 600;
      ">${locationData.title}</h3>
      <p style="
        margin: 0 0 12px 0;
        font-size: 13px;
        color: #666;
        line-height: 1.4;
      ">${locationData.address}</p>
      <div style="
        display: flex;
        gap: 8px;
        justify-content: center;
        margin-top: 12px;
      ">
        <a href="https://waze.com/ul?q=${encodeURIComponent(
          locationData.address
        )}&navigate=yes" 
           target="_blank" 
           style="
             background: linear-gradient(135deg, #01727a 0%, #0288a3 100%);
             color: white;
             padding: 6px 12px;
             text-decoration: none;
             border-radius: 20px;
             font-size: 11px;
             font-weight: 500;
             box-shadow: 0 2px 6px rgba(1, 114, 122, 0.3);
           ">
          🗺️ ווייז
        </a>
        <a href="https://maps.google.com/?q=${encodeURIComponent(
          locationData.address
        )}" 
           target="_blank"
           style="
             background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
             color: white;
             padding: 6px 12px;
             text-decoration: none;
             border-radius: 20px;
             font-size: 11px;
             font-weight: 500;
             box-shadow: 0 2px 6px rgba(66, 133, 244, 0.3);
           ">
          📍 גוגל מפות
        </a>
      </div>
    </div>
  `;

  // Component to fit bounds when showing both locations
  const FitBounds = () => {
    const map = useMap();

    useEffect(() => {
      if (location === "both") {
        const bounds = L.latLngBounds([
          locations.herzliya.position,
          locations.beersheba.position,
        ]);
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }, [map]);

    return null;
  };

  const mapCenter =
    location === "both" ? locations.both.center : locations[location].position;

  const mapZoom =
    location === "both" ? locations.both.zoom : locations[location].zoom;

  return (
    <div
      style={{
        width: "100%",
        height: height,
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(1, 114, 122, 0.15)",
        border: "1px solid rgba(1, 114, 122, 0.1)",
      }}
    >
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        {/* Beautiful CartoDB Positron tile layer - clean and medical-friendly */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={19}
        />

        {/* Custom zoom control positioned on the left */}
        <div
          className="leaflet-control-zoom leaflet-bar leaflet-control"
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            zIndex: 1000,
          }}
        ></div>

        <FitBounds />

        {/* Render markers based on location prop */}
        {location === "both" ? (
          // Show both markers
          Object.keys(locations).map((key) => {
            if (key !== "both") {
              const locationData = locations[key];
              return (
                <Marker
                  key={key}
                  position={locationData.position}
                  icon={createCustomIcon()}
                >
                  <Popup>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: createPopupContent(locationData),
                      }}
                    />
                  </Popup>
                </Marker>
              );
            }
            return null;
          })
        ) : (
          // Show single marker
          <Marker
            position={locations[location].position}
            icon={createCustomIcon()}
          >
            <Popup>
              <div
                dangerouslySetInnerHTML={{
                  __html: createPopupContent(locations[location]),
                }}
              />
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Custom CSS for medical-themed styling */}
      <style jsx>{`
        .custom-medical-marker {
          background: none !important;
          border: none !important;
        }

        .leaflet-popup-content-wrapper {
          border-radius: 15px !important;
          box-shadow: 0 8px 25px rgba(1, 114, 122, 0.2) !important;
        }

        .leaflet-popup-tip {
          background: white !important;
        }

        .leaflet-control-zoom {
          border-radius: 10px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .leaflet-control-zoom a {
          background: white !important;
          color: #01727a !important;
          border-radius: 8px !important;
          border: none !important;
          font-weight: bold !important;
        }

        .leaflet-control-zoom a:hover {
          background: #f0f8ff !important;
          color: #01727a !important;
        }
      `}</style>
    </div>
  );
};

export default LeafletMap;
